import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

import { createInvestmentCompanyTableConfig, TableRowAction } from './investment-company-table.config';
import {
  InvestmentCompanyListSummary,
  InvestmentCompanyRow,
  investmentPaymentLabel,
  investmentPaymentVariant,
  roundMoney,
  toInvestmentCompanyRow
} from '../../models/investment-company.model';
import { InvestmentCompanyEntityService } from '../../services/investment-company-entity.service';
import { InvestmentCompanyFormDialogComponent } from '../investment-company-form-dialog/investment-company-form-dialog.component';

import { InvestmentInCompaniesService } from '../../../../../modules/investment/incompanies/investment-incompanies.service';
import { InvestmentInCompaniesDialogPayComponent } from '../../../../../modules/investment/incompanies/investment-incompanies-dialog-pay.component';
import { AccountingManualFormComponent } from '../../../../../modules/accounting/manual/accounting-manual-form.component';
import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { investmentListPayload, investmentSearchFromState } from '../../../shared/investment-list.util';

@Component({
  standalone: false,
  selector: 'app-investment-company-list',
  templateUrl: './investment-company-list.components.html',
  styleUrls: ['./investment-company-list.components.css']
})
export class InvestmentCompanyListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('investmentSummary') investmentSummaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance';
  title = 'Inversiones en empresas';
  subTitle = 'Consulta, registra y paga tus participaciones, préstamos y créditos a empresas';

  quickSearch = '';
  totals = { count: 0, total: 0, paid: 0, pending: 0, netBook: 0 };
  selectedUids: string[] = [];

  private rows: InvestmentCompanyRow[] = [];
  private selectedRows: Record<string, InvestmentCompanyRow> = {};
  private summary: InvestmentCompanyListSummary | null = null;
  private totalItems = 0;
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  private readonly quickSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private entity: InvestmentCompanyEntityService,
    private inCompaniesService: InvestmentInCompaniesService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private loading: LoadingDialogService,
    private cdr: ChangeDetectorRef,
  ) { }

  get hasSelection(): boolean {
    return this.selectedUids.length > 0;
  }

  ngOnInit(): void {
    const money = (val: number) => Utils.FormatMoney(val ?? 0) + ' €';
    const date = (val: any) => this.formatDateValue(val);

    this.tableConfig = createInvestmentCompanyTableConfig(
      {
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
          [TableRowAction.DownloadImage]: (row) => this.downloadFileImage(row),
          [TableRowAction.DownloadPdf]: (row) => this.downloadFilePdf(row),
          [TableRowAction.Pay]: (row) => this.pay(row),
          [TableRowAction.Contabilizar]: () => this.addManualAccountMovements(),
        },
        selectable: true,
        columns: [
          { key: 'investmentDescription', label: 'Descripción', sortable: true, sortKey: 'InvestmentDescription', ellipsis: true, onCellClick: (row: InvestmentCompanyRow) => this.edit(row) },
          {
            key: 'accountingModelsNumber', label: 'Cuenta', sortable: true, sortKey: 'AccountNumber', ellipsis: true,
            formatFn: (val: string, row: InvestmentCompanyRow) => [val, row && row.accountingModelsName].filter(Boolean).join(' · '),
          },
          { key: 'investmentDate', label: 'Fecha de inversión', sortable: true, sortKey: 'InvestmentDate', formatFn: date },
          { key: 'investmentValue', label: 'Total', sortable: true, sortKey: 'InvestmentValue', formatFn: money },
          { key: 'paidAmount', label: 'Pagado', sortable: true, sortKey: 'PaidAmount', formatFn: money },
          { key: 'pendingAmount', label: 'Pendiente', sortable: true, sortKey: 'PendingAmount', formatFn: money },
          {
            key: 'paymentStatus', label: 'Pago', sortable: true, sortKey: 'PaymentStatus',
            badge: {
              variantFn: (_: any, row: InvestmentCompanyRow) => investmentPaymentVariant(row),
              labelFn: (_: any, row: InvestmentCompanyRow) => investmentPaymentLabel(row),
            },
          },
          { key: 'netBookValue', label: 'Valor neto contable', sortable: true, sortKey: 'NetBookValue', formatFn: money },
          { key: 'numberOfUnitsOrSharesInvested', label: 'Participaciones', sortable: true, sortKey: 'NumberOfUnitsOrSharesInvested', hidden: true, formatFn: (val: number) => val ? Utils.FormatMoney(val) : '' },
          { key: 'percentageOfProfitabilityOrInterest', label: 'Rentabilidad', sortable: true, sortKey: 'PercentageOfProfitabilityOrInterest', hidden: true, formatFn: (val: number) => val ? Utils.FormatMoney(val) + ' %' : '' },
          { key: 'termInMonths', label: 'Plazo (meses)', sortable: true, sortKey: 'TermInMonths', hidden: true, formatFn: (val: number) => val ? String(val) : '' },
          { key: 'lastBalanceSheetDate', label: 'Último balance', sortable: true, sortKey: 'LastBalanceSheetDate', hidden: true, formatFn: date },
          {
            key: 'generatesReturns', label: 'Rendimientos', sortable: false, hidden: true,
            badge: {
              variantFn: (val: boolean) => val ? 'success' : 'neutral',
              labelFn: (val: boolean) => val ? 'Genera' : 'No genera',
            },
          },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Contabilizar (asiento libre)', icon: 'show_chart', type: 'button', callback: () => this.addManualAccountMovements() },
        ],
        rowConfig: {
          onToggleSelect: (row: InvestmentCompanyRow, selected: boolean) => this.toggleSelection(row, selected),
        },
      },
      {
        filterView: 'investmentCompany',
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.investmentSummaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const search = investmentSearchFromState(state);
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  reload(): void {
    this.load();
  }

  create(): void {
    InvestmentCompanyFormDialogComponent.open(this.dialog, {})
      .subscribe(saved => { if (saved) this.load(); });
  }

  edit(row: InvestmentCompanyRow): void {
    if (!row || !row.uid) return;
    InvestmentCompanyFormDialogComponent.open(this.dialog, { uid: row.uid })
      .subscribe(saved => { if (saved) this.load(); });
  }

  pay(row: InvestmentCompanyRow): void {
    InvestmentInCompaniesDialogPayComponent.open(this.dialog, { investment: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  delete(row: InvestmentCompanyRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar inversión',
      subtitle: row.investmentDescription || undefined,
      message: 'Se eliminarán la inversión y todos sus pagos junto con sus asientos contables. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.rowDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando inversión…',
      errorText: 'Ha ocurrido un error al eliminar la inversión',
      action: () => this.entity.delete(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Inversión eliminada correctamente');
      this.unselect(row.uid);
      this.load();
    });
  }

  downloadAttachment(row: InvestmentCompanyRow): void {
    if (row.isPdf) {
      this.downloadFilePdf(row);
    } else if (row.isImage) {
      this.downloadFileImage(row);
    }
  }

  downloadFileImage(row: InvestmentCompanyRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Descargando documento…', this.inCompaniesService.downloadFileImage(row.uid))
      .then(blob => FileSaver.saveAs(blob, Utils.GetFileName('Imagen_Adjunta_Inversion', consts.EXT_PNG)))
      .catch(() => this.notify.error('No se pudo descargar la imagen'));
  }

  downloadFilePdf(row: InvestmentCompanyRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Descargando documento…', this.inCompaniesService.downloadFilePDF(row.uid))
      .then(blob => FileSaver.saveAs(blob, Utils.GetPdfName('Pdf_Adjunta_Inversion')))
      .catch(() => this.notify.error('No se pudo descargar el PDF'));
  }

  addManualAccountMovements(): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.dialog.open(AccountingManualFormComponent, {
      width: '90vw',
      maxWidth: '96vw',
      height: '70%',
      data: { accountingMovementId: '' }
    }).afterClosed().subscribe((result: string) => {
      if (!Utils.IsNullOrEmpty(result) && result === 'OK') this.load();
    });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);
    this.entity.list(investmentListPayload(this.lastState))
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(item => item.__expanded).map(item => item.uid));
        this.rows = result.items.filter(item => !!item && !!item.uid).map(item => {
          const selected = this.selectedUids.indexOf(item.uid) !== -1;
          const row: InvestmentCompanyRow = toInvestmentCompanyRow(item, expanded.has(item.uid), selected);
          if (selected) this.selectedRows[item.uid] = row;
          return row;
        });
        this.summary = result.summary;
        this.totalItems = result.total;
        this.recomputeTotals();
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de inversiones en empresas');
      });
  }

  private toggleSelection(row: InvestmentCompanyRow, selected: boolean): void {
    if (!row || !row.uid) return;
    if (selected) {
      if (this.selectedUids.indexOf(row.uid) === -1) this.selectedUids.push(row.uid);
      this.selectedRows[row.uid] = row;
    } else {
      this.unselect(row.uid);
    }
    this.recomputeTotals();
    this.cdr.markForCheck();
  }

  private unselect(uid: string): void {
    this.selectedUids = this.selectedUids.filter(item => item !== uid);
    delete this.selectedRows[uid];
  }

  private recomputeTotals(): void {
    if (!this.hasSelection) {
      const summary = this.summary;
      this.totals = {
        count: this.totalItems,
        total: roundMoney(summary ? summary.investmentValue : 0),
        paid: roundMoney(summary ? summary.paidAmount : 0),
        pending: roundMoney(summary ? summary.pendingAmount : 0),
        netBook: roundMoney(summary ? summary.netBookValue : 0),
      };
      return;
    }
    const source = this.selectedUids.map(uid => this.selectedRows[uid]).filter(row => !!row);
    this.totals = {
      count: source.length,
      total: roundMoney(source.reduce((acc, row) => acc + (Number(row.investmentValue) || 0), 0)),
      paid: roundMoney(source.reduce((acc, row) => acc + (Number(row.paidAmount) || 0), 0)),
      pending: roundMoney(source.reduce((acc, row) => acc + Math.max(Number(row.pendingAmount) || 0, 0), 0)),
      netBook: roundMoney(source.reduce((acc, row) => acc + (Number(row.netBookValue) || 0), 0)),
    };
  }

  private rowDetails(row: InvestmentCompanyRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.accountingModelsNumber) details.push({ label: 'Cuenta', value: [row.accountingModelsNumber, row.accountingModelsName].filter(Boolean).join(' · ') });
    const date = this.formatDateValue(row.investmentDate);
    if (date) details.push({ label: 'Fecha de inversión', value: date });
    details.push({ label: 'Total', value: Utils.FormatMoney(row.investmentValue ?? 0) + ' €' });
    details.push({ label: 'Pagado', value: Utils.FormatMoney(row.paidAmount ?? 0) + ' €' });
    return details;
  }

  private formatDateValue(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '';
    return formatDate(date, 'dd/MM/yyyy', 'es-ES');
  }
}
